import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { isInAppWebViewNoise, isOfflineError } from 'src/utils/sentry/filters'

export { SENTRY_DENY_URLS, SENTRY_IGNORE_ERRORS } from 'src/utils/sentry/filters'

export const SENTRY_SEVERITY = {
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
} as const

const PCODE_ROUTES = ['mkt01', 'mkt02']

export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  /**
   * Sentry로 이벤트가 전송되기 직전에 호출되는 필터/정제 함수
   * null을 반환하면 해당 이벤트는 Sentry로 전송되지 않음
   *
   * 문자열/regex 노이즈 필터는 SENTRY_IGNORE_ERRORS(ignoreErrors)에서 처리.
   * 여기서는 hint 기반 로직 필터만 담당.
   */

  if (isOfflineError(hint)) return null
  if (isInAppWebViewNoise(event)) return null

  // /mkt01/:pcode/:requestID/... → route, pCode (pCode 세그먼트가 있는 라우트에서만 태그로 붙인다)
  // /terms/:requestID/... 는 두 번째 세그먼트가 requestID라 pCode 태그를 붙이지 않는다
  const [, route, pCode] = window.location.pathname.split('/')
  event.tags = {
    ...event.tags,
    ...(route && { route }),
    ...(pCode && PCODE_ROUTES.includes(route) && { pCode }),
  }

  return event
}

export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint,
): Breadcrumb | null => {
  if (breadcrumb.category === 'console') {
    return breadcrumb.level === SENTRY_SEVERITY.Error ? breadcrumb : null
  }
  if (['sentry.transaction', 'sentry.performance'].includes(breadcrumb.category ?? '')) {
    return null
  }

  return breadcrumb
}

const applyScopeMeta = (
  scope: Sentry.Scope,
  tags?: Record<string, string>,
  extra?: Record<string, unknown>,
) => {
  Object.entries(tags ?? {}).forEach(([k, v]) => scope.setTag(k, v))

  if (extra) {
    scope.setExtras(extra)
  }
}

interface CaptureErrorOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  fingerprint?: string[]
}

export const captureError = (error: unknown, options: CaptureErrorOptions = {}) => {
  const { level = SENTRY_SEVERITY.Error, tags, extra, fingerprint } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)
    if (fingerprint) {
      scope.setFingerprint(fingerprint)
    }
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)))
  })
}

interface CaptureMessageOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

export const captureMessage = (message: string, options: CaptureMessageOptions = {}) => {
  const { level = SENTRY_SEVERITY.Info, tags = {}, extra } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)

    Sentry.captureMessage(message, level)
  })
}

export const toApiError = (error: Error): Error => {
  error.name = 'ApiError'
  return error
}

export const toServerError = (error: Error): Error => {
  error.name = 'ServerError'
  return error
}

export const toNavigationError = (error: Error): Error => {
  error.name = 'NavigationError'
  return error
}
